import Box from '@mui/material/Box';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { hasObjectInstances } from 'services/entities/VariablesEntity';
import type { VariableNode } from 'services/entities/VariablesEntity';
import { useAppSelector } from '../grid/reduxStore/Store';
import { selectContentId, selectContentType } from '../grid/reduxStore/documentSettingsSlice';
import { ContentType } from 'utils/getContentType';
import SummaryTitleWithTooltip from './SummaryTitleWithTooltip';
import { AccordionDetails } from './AccordionDetails';
import { CrmEmptyState } from './CrmEmptyState';
import { useCrmEmptyState } from './useCrmEmptyState';
import { deriveArrayFieldSchema, isFieldCollectionEntry } from './utils';

interface VariableFoldersProps {
  activeNode?: Partial<Pick<VariableNode, 'variables' | 'shape' | 'path'>> | VariableNode[];
  selectNode: (node: VariableNode) => void;
  isEditor?: boolean;
}

const VariableFolders = ({ activeNode, selectNode, isEditor }: VariableFoldersProps) => {
  const crmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  // Mirrors the two-flag gate used elsewhere for this feature (e.g. useContactOptions.tsx) —
  // crmDealLinking alone isn't sufficient until useVariablesBff is also on for this tenant.
  const crmDealLinkingFlags = useFeatureFlag([FeatureFlags.crmDealLinking, FeatureFlags.useVariablesBff]);
  const crmDealLinkingEnabled = Boolean(
    crmDealLinkingFlags[FeatureFlags.crmDealLinking] && crmDealLinkingFlags[FeatureFlags.useVariablesBff]
  );
  const contentId = useAppSelector(selectContentId);
  const contentType = useAppSelector(selectContentType);

  const arrayActiveNode =
    crmPricingEmbedEnabled && !Array.isArray(activeNode) && activeNode?.shape?.type === 'array' ? (activeNode as VariableNode) : null;

  const isCrmArrayContext = crmDealLinkingEnabled && arrayActiveNode !== null;
  const { emptyState, hasAnyCrmObjectLinked } = useCrmEmptyState(contentId, arrayActiveNode ?? undefined, isCrmArrayContext);

  const currentItems: VariableNode[] = Array.isArray(activeNode)
    ? activeNode
    : arrayActiveNode
    ? deriveArrayFieldSchema(arrayActiveNode)
    : activeNode?.variables ?? [];
  const variableContainerStyle = isEditor ? { maxHeight: '138px', overflow: 'auto' } : { minHeight: '160px' };

  // A template never has a linked deal, so the "link a deal" empty state never applies here.
  const showCrmEmptyState = isCrmArrayContext && emptyState !== null && contentType !== ContentType.TEMPLATE;
  if (showCrmEmptyState) {
    return (
      <Box data-testid="variables-folder-container" sx={variableContainerStyle} overflow={'auto'}>
        <CrmEmptyState emptyState={emptyState} contentId={contentId} hasLinkedObjects={hasAnyCrmObjectLinked} />
      </Box>
    );
  }

  return (
    <Box data-testid="variables-folder-container" sx={variableContainerStyle} overflow={'auto'}>
      {currentItems.map((item) => {
        const { friendlyName, variables = [], name, shape } = item;

        const isArrayEntry = crmPricingEmbedEnabled && shape?.type === 'array';
        const isExpandableArray = !isFieldCollectionEntry(item) ? shape?.grouped ?? hasObjectInstances(item) : hasObjectInstances(item);
        if (isArrayEntry && isExpandableArray) {
          const isNestedArray = arrayActiveNode !== null;
          return (
            <SummaryTitleWithTooltip
              key={name}
              group={friendlyName}
              expanded={false}
              isRounded={!isEditor}
              isArray={!isNestedArray}
              isNested={isNestedArray}
              onActiveUpdate={() => selectNode(item)}
            />
          );
        }

        // Derived field entry, flat array, or empty array: terminal item with badge.
        if (isArrayEntry) {
          return <AccordionDetails variableMeta={item} isEditor={isEditor} key={name} />;
        }

        if (variables.length > 0) {
          return (
            <SummaryTitleWithTooltip
              key={name}
              group={friendlyName}
              expanded={false}
              isRounded={!isEditor}
              onActiveUpdate={() => selectNode(item)}
            />
          );
        }

        if (friendlyName) {
          return <AccordionDetails variableMeta={item} isEditor={isEditor} key={name} />;
        }

        return null;
      })}
    </Box>
  );
};

export default VariableFolders;
